<template>
  <div class="bg-white rounded-lg border border-gray-200 p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h3 class="text-lg font-medium text-gray-900">
          {{ $t('BUSINESS_DATA.ITEMS.TITLE') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('BUSINESS_DATA.ITEMS.DESCRIPTION') }}
        </p>
      </div>
      <woot-button @click="$emit('add-data-item')">
        <fluent-icon icon="document" size="16" class="mr-2" />
        {{ $t('BUSINESS_DATA.ITEMS.ADD_NEW') }}
      </woot-button>
    </div>
    
    <!-- Pagination Table -->
    <PaginationTable
      :items="dataItems"
      :categories="categories"
      :pagination="pagination"
      :is-loading="isLoading"
      :is-searching="isSearching"
      empty-icon="document"
      empty-title-key="BUSINESS_DATA.ITEMS.EMPTY.TITLE"
      empty-description-key="BUSINESS_DATA.ITEMS.EMPTY.DESCRIPTION"
      add-button-key="BUSINESS_DATA.ITEMS.ADD_NEW"
      @search="$emit('search', $event)"
      @filter-change="$emit('filter-change', $event)"
      @page-change="$emit('page-change', $event)"
      @add-item="$emit('add-data-item')"
      @edit-item="$emit('edit-data-item', $event)"
      @toggle-status="toggleItemStatus"
      @delete-item="deleteItem"
    >
      <template #default="{ items, onEdit, onToggleStatus, onDelete }">
        <div class="space-y-4">
          <div 
            v-for="item in items" 
            :key="item.id" 
            class="data-item border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <!-- Title -->
                <div class="flex items-center gap-2 mb-2">
                  <fluent-icon icon="document" size="16" class="text-purple-500" />
                  <h4 class="font-medium text-gray-900">{{ item.title }}</h4>
                  <span 
                    v-if="item.category"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    {{ item.category }}
                  </span>
                </div>
                
                <!-- Description -->
                <p class="text-gray-600 text-sm mb-3">{{ item.description }}</p>
                
                <!-- Meta info -->
                <div class="flex items-center gap-4 text-xs text-gray-500">
                  <span v-if="item.business_category" class="inline-flex items-center">
                    <span
                      class="px-2 py-1 rounded-full"
                      :style="{
                        backgroundColor: item.business_category.color,
                        color: getTextColor(item.business_category.color)
                      }"
                    >
                      {{ item.business_category.name }}
                    </span>
                  </span>
                  <span class="font-mono bg-gray-100 px-2 py-1 rounded">ID: {{ item.id }}</span>
                  <span>{{ $t('BUSINESS_DATA.ITEMS.META.CREATED') }}: {{ formatDate(item.created_at) }}</span>
                  <span 
                    :class="item.is_active ? 'text-green-600' : 'text-red-600'"
                  >
                    {{ item.is_active ? $t('BUSINESS_DATA.ITEMS.META.ACTIVE') : $t('BUSINESS_DATA.ITEMS.META.INACTIVE') }}
                  </span>
                </div>
              </div>
              
              <!-- Actions -->
              <div class="flex items-center gap-2 ml-4">
                <woot-button
                  variant="clear"
                  size="small"
                  @click="onEdit(item)"
                >
                  <fluent-icon icon="edit" size="14" />
                </woot-button>
                
                <woot-button
                  variant="clear"
                  size="small"
                  color-scheme="secondary"
                  @click="onToggleStatus(item)"
                >
                  <fluent-icon 
                    :icon="item.is_active ? 'eye-hide' : 'eye-show'" 
                    size="14" 
                  />
                </woot-button>
                
                <woot-button
                  variant="clear"
                  size="small"
                  color-scheme="danger"
                  @click="onDelete(item)"
                >
                  <fluent-icon icon="delete" size="14" />
                </woot-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </PaginationTable>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import PaginationTable from './PaginationTable.vue';
function getTextColor(backgroundColor) {
  if (!backgroundColor) return '#000000';
  const hex = String(backgroundColor).replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export default {
  name: 'DataItemsSection',
  
  components: {
    PaginationTable
  },
  
  props: {
    dataItems: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    }
  },

  emits: ['refresh', 'add-data-item', 'edit-data-item', 'search', 'filter-change', 'page-change'],
  
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    }
  },
  
  methods: {
    getTextColor,
    async toggleItemStatus(item) {
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/business_data_items/${item.id}`, {
          is_active: !item.is_active
        });
        this.$emit('refresh');
        useAlert(
          item.is_active 
            ? this.$t('BUSINESS_DATA.ITEMS.DEACTIVATED_SUCCESS')
            : this.$t('BUSINESS_DATA.ITEMS.ACTIVATED_SUCCESS'),
          'success'
        );
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), 'error');
      }
    },
    
    async deleteItem(item) {
      if (!window.confirm(this.$t('BUSINESS_DATA.ITEMS.DELETE_CONFIRM'))) {
        return;
      }
      
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_data_items/${item.id}`);
        this.$emit('refresh');
        useAlert(this.$t('BUSINESS_DATA.ITEMS.DELETED_SUCCESS'), 'success');
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED'), 'error');
      }
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    }
  },
};
</script>
